import React from 'react';
import { useFamily } from '../../context/FamilyContext';
import { usePermissions } from '../../hooks/usePermissions';
import { formatCurrency, getCategoryIcon } from '../../utils/expenseUtils';
import { updateRecurringExpense, deleteRecurringExpense } from '@coparent/shared/src/services/expenseService';
import { DAYS_OF_WEEK_SHORT, RECURRING_FREQUENCIES } from '@coparent/shared/src/utils/constants';
import ChildDots from '../common/ChildDots';
import { HiPencil, HiTrash, HiPause, HiPlay } from 'react-icons/hi';
import toast from 'react-hot-toast';

const getScheduleLabel = (re) => {
  const freq = RECURRING_FREQUENCIES.find(f => f.value === re.frequency);
  const freqLabel = freq?.label || re.frequency;

  if (re.frequency === 'monthly') {
    const d = re.dayOfMonth;
    const suffix = d === 1 ? 'st' : d === 2 ? 'nd' : d === 3 ? 'rd' : 'th';
    return `${freqLabel} on the ${d}${suffix}`;
  }
  if (re.frequency === 'weekly' || re.frequency === 'biweekly') {
    const day = DAYS_OF_WEEK_SHORT[re.dayOfWeek] || '';
    return `${freqLabel} on ${day}`;
  }
  return freqLabel;
};

const RecurringExpenseList = ({ recurringExpenses, onEdit }) => {
  const { familyId, family, childrenMap } = useFamily();
  const { canDelete } = usePermissions();

  const handleToggleActive = async (re) => {
    try {
      await updateRecurringExpense(familyId, re.id, { isActive: !re.isActive });
      toast.success(re.isActive ? 'Paused' : 'Resumed');
    } catch {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this recurring expense? Past auto-created expenses will remain.')) return;
    try {
      await deleteRecurringExpense(familyId, id);
      toast.success('Recurring expense deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  if (!recurringExpenses.length) {
    return (
      <div className="text-center py-10 text-ink-300">
        <p className="text-base font-semibold">No recurring expenses</p>
        <p className="text-sm mt-1">Set up bills or costs that repeat on a schedule.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {recurringExpenses.map(re => {
        const paidByProfile = family?.parentProfiles?.[re.paidBy];
        return (
          <div
            key={re.id}
            className={`card-hover p-4 ${!re.isActive ? 'opacity-50' : ''}`}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <span className="text-2xl">{getCategoryIcon(re.category)}</span>
                <div>
                  <p className="font-semibold text-ink-900 text-sm">{re.description}</p>
                  <p className="text-xs text-ink-400 mt-0.5">
                    {getScheduleLabel(re)} &middot; Paid by{' '}
                    <span className="font-semibold" style={{ color: paidByProfile?.color }}>
                      {paidByProfile?.displayName}
                    </span>
                  </p>
                  <p className="text-xs text-ink-400 mt-0.5">
                    {re.splitRatio}% split &middot; {formatCurrency(re.amount * (re.splitRatio / 100))} owed per occurrence
                  </p>
                  {re.childIds?.length > 0 && (
                    <div className="mt-1">
                      <ChildDots childIds={re.childIds} childrenMap={childrenMap} showNames />
                    </div>
                  )}
                  {re.note && (
                    <p className="text-xs text-ink-300 mt-1">{re.note}</p>
                  )}
                  {!re.isActive && (
                    <p className="text-[0.625rem] text-ink-300 mt-1 italic">Paused</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <p className="font-bold text-ink-900 mr-2">{formatCurrency(re.amount)}</p>
                <button
                  onClick={() => handleToggleActive(re)}
                  className="w-7 h-7 rounded-lg flex items-center justify-center text-ink-300 hover:text-ink-600 hover:bg-surface-100 transition-colors"
                  title={re.isActive ? 'Pause' : 'Resume'}
                >
                  {re.isActive ? <HiPause className="text-sm" /> : <HiPlay className="text-sm" />}
                </button>
                <button
                  onClick={() => onEdit(re)}
                  className="w-7 h-7 rounded-lg flex items-center justify-center text-ink-300 hover:text-ink-600 hover:bg-surface-100 transition-colors"
                  title="Edit"
                >
                  <HiPencil className="text-sm" />
                </button>
                {canDelete && (
                  <button
                    onClick={() => handleDelete(re.id)}
                    className="w-7 h-7 rounded-lg flex items-center justify-center text-ink-300 hover:text-accent-600 hover:bg-accent-50 transition-colors"
                    title="Delete"
                  >
                    <HiTrash className="text-sm" />
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default RecurringExpenseList;
